import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { adopt } from 'react-adopt';
import { Query, Mutation } from '@apollo/react-components';
import { ApolloConsumer } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import get from 'lodash/get';
import last from 'lodash/last';
import useDidMount from '@rooks/use-did-mount';
import useWillUnmount from '@rooks/use-will-unmount';
import root from 'window-or-global';

import GoogleContext from 'data/services/google';
import { NotFoundError } from 'routing/errors';
import ApplicationPage from 'site/application-page';
import { GlobalConsumer } from 'site/context';
import ContributePage from './contribute-page';

const GET_ROLES = gql`
    query GetCampaignRoles($id: ID!) {
        my {
            roles: rolesFor(type: "campaign", id: $id)
            user {
                id
                username
            }
        }
    }
`;

const GET_CAMPAIGN = gql`
    query GetCampaign($slug: String!) {
        campaign: campaignBySlug(slug: $slug) {
            id
            slug
            name
            eventsEndAt
            isComplete
            isDonationOnly

            stats {
                amountRaisedActual
                amountRaisedEstimate
                eventRemainingCount
                eventFinalizedCount
                performanceActualTotal
            }

            performances {
                id
                isFinal
            }
        }
    }
`;

const GET_CONTRIBUTION = gql`
    query GetContribution($id: ID!) {
        contribution: contributionById(id: $id) {
            id
            invoices {
                id
                createdAt
                amount
                status
            }
        }
    }
`;

const CREATE_CONTRIBUTION = gql`
    mutation CreateContribution($data: ContributionInput!) {
        createContribution(data: $data)
    }
`;

/* eslint-disable react/prop-types */
const Wrapper = adopt(
    {
        global: <GlobalConsumer />,
        client: ({ render }) => <ApolloConsumer>{render}</ApolloConsumer>,

        campaignQuery: ({ slug, render }) => (
            <Query query={GET_CAMPAIGN} variables={{ slug }}>
                {render}
            </Query>
        ),

        rolesQuery: ({ render, campaignQuery }) => {
            const id = get(campaignQuery, 'data.campaign.id');
            return (
                <Query query={GET_ROLES} variables={{ id }} skip={!id}>
                    {render}
                </Query>
            );
        },

        createContribution: ({ render, campaignQuery, slug }) => {
            const id = get(campaignQuery, 'data.campaign.id');
            return (
                <Mutation
                    skip={!id}
                    mutation={CREATE_CONTRIBUTION}
                    refetchQueries={[
                        { query: GET_ROLES, variables: { id } },
                        { query: GET_CAMPAIGN, variables: { slug } },
                    ]}
                    awaitRefetchQueries
                >
                    {render}
                </Mutation>
            );
        },
    },
    ({ client, global, campaignQuery, rolesQuery, ...rest }) => ({
        client,
        error: campaignQuery.error,
        loading: campaignQuery.loading,
        setRecentContribution: global.setRecentContribution,
        campaign: get(campaignQuery, 'data.campaign'),
        roles: get(rolesQuery, 'data.my.roles', []),

        user: global.user,
        getLockedContributionReferrer: global.getLockedContributionReferrer,
        showFlash: global.showFlash,
        ...rest,
    })
);
/* eslint-enable react/prop-types */

const ContributePageContainer = ({ match, history, location }) => {
    const { pageview } = useContext(GoogleContext);

    let { step } = match.params;
    let type = null;

    if (location.pathname.endsWith('/pledge')) {
        step = 'amount';
        type = 'pledge';
    } else if (location.pathname.endsWith('/donate')) {
        step = 'amount';
        type = 'donate';
    } else if (location.pathname.endsWith('/contribute')) {
        step = 'type';
    }

    useDidMount(() => root.zE(() => root.zE.hide()));
    useWillUnmount(() => root.zE(() => root.zE.show()));

    return (
        <ApplicationPage header="minimal">
            <Wrapper slug={match.params.slug}>
                {({
                    client,
                    error,
                    loading,
                    campaign,
                    roles,
                    user,
                    createContribution,
                    setRecentContribution,
                    getLockedContributionReferrer,
                    ...props
                }) => {
                    if (error) throw error;
                    if (loading) return null;
                    if (!campaign) throw new NotFoundError('Campaign not found');

                    const lockedFundraiser = getLockedContributionReferrer(campaign.id);

                    // Safety measure to ensure that if the user is a fundraiser
                    // and they haven't come from another fundraiser's page, that
                    // they are pre-selected as the referrer
                    const backupFundraiser = roles.includes('fundraiser') ? user.username : null;

                    return (
                        <ContributePage
                            step={step}
                            campaign={campaign}
                            navigate={history.push}
                            type={campaign.isDonationOnly ? 'donate' : type}
                            fundraiser={lockedFundraiser || backupFundraiser}
                            isReferrerLocked={!!lockedFundraiser}
                            createContribution={async data => {
                                const response = await createContribution({ variables: { data } });
                                const contributionId = get(response, 'data.createContribution');
                                let completed = data.type === 'pledge';

                                // If the user donated, we'll poll the invoice for 10 seconds to
                                // determine if the payment succeeded or not
                                if (data.type === 'donation') {
                                    let tries = 0;

                                    do {
                                        // Refetch the contribution
                                        const query = await client.query({
                                            query: GET_CONTRIBUTION,
                                            variables: { id: contributionId },
                                            fetchPolicy: 'network-only',
                                        });

                                        if (query.error) throw error;

                                        const invoice = last(
                                            get(query, 'data.contribution.invoices').filter(
                                                ({ amount }) => amount === data.amount
                                            )
                                        );

                                        // Check it's status
                                        if (invoice.status === 'paid') {
                                            completed = true;
                                        } else if (['error', 'failed'].includes(invoice.status)) {
                                            throw new Error('Donation failed to process');
                                        }

                                        // Wait 1 second
                                        await new Promise(resolve => setTimeout(resolve, 1000));

                                        tries += 1;
                                    } while (tries < 10 && !completed);
                                }

                                if (completed) {
                                    pageview(`/${campaign.slug}/contribute/thanks`);

                                    await setRecentContribution({
                                        campaign: data.campaign,
                                        type: data.type,
                                        amount: data.amount,
                                    });
                                }

                                return { completed };
                            }}
                            {...props}
                        />
                    );
                }}
            </Wrapper>
        </ApplicationPage>
    );
};

ContributePageContainer.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }).isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            slug: PropTypes.string.isRequired,
            step: PropTypes.string,
        }).isRequired,
    }).isRequired,
    location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
    }).isRequired,
};

export default ContributePageContainer;
